import type { NodeData, Status } from './data';

// `process.env.API_BASE` is substituted inline by Parcel at BUILD time from the
// shell environment (Parcel doesn't auto-load .env files in this setup). The
// `process` type lives in src/globals.d.ts — it must NOT be declared inside this
// module, or Parcel treats `process` as a local binding and skips substitution
// (→ "process is not defined" in the browser).
//
//   • dev   — `pnpm frontend` sets API_BASE=http://localhost:3001 (see the
//             frontend "start" script); Parcel and the backend run on separate ports.
//   • prod  — API_BASE comes from the Docker build arg / Dokploy build-time var;
//             default empty = same-origin (the Bun backend serves this bundle).
//
// Trailing slash stripped so `${BASE}/api` is clean.
export const BASE = (process.env.API_BASE ?? 'http://localhost:3001').replace(/\/+$/, '');

export interface ApiNode {
  id:          string;
  label:       string;
  description: string;
  status:      Status;
  parent_id:   string | null;
  child_order: number;
  x:           number | null;
  y:           number | null;
}

export interface PositionUpdate {
  id: string;
  x:  number;
  y:  number;
}

export interface Page {
  id:         string;
  title:      string;
  created_at: string;
  node_count: number;
}

export interface ApiEvent {
  id:         string;
  node_id:    string;
  status:     Status | null;
  note:       string;
  author:     string;
  // Rename record: `label` is the new name applied by this event, `prev_label`
  // the name the node had before. Both null when the event doesn't rename.
  label:      string | null;
  prev_label: string | null;
  created_at: string;
}

// ─── Pages ──────────────────────────────────────────────────────────────────

export async function fetchPages(): Promise<Page[]> {
  const res = await fetch(`${BASE}/api/pages`);
  if (!res.ok) throw new Error(`fetchPages: ${res.status}`);
  return res.json();
}

export async function createPage(title: string): Promise<Page> {
  const res = await fetch(`${BASE}/api/pages`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ title }),
  });
  if (!res.ok) throw new Error(`createPage: ${res.status}`);
  return res.json();
}

export async function renamePage(id: string, title: string): Promise<void> {
  const res = await fetch(`${BASE}/api/pages/${encodeURIComponent(id)}`, {
    method:  'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ title }),
  });
  if (!res.ok) throw new Error(`renamePage: ${res.status}`);
}

export async function deletePage(id: string): Promise<void> {
  const res = await fetch(`${BASE}/api/pages/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`deletePage: ${res.status}`);
}

// ─── Reads ────────────────────────────────────────────────────────────────────

export async function fetchNodes(pageId: string): Promise<ApiNode[]> {
  const res = await fetch(`${BASE}/api/pages/${encodeURIComponent(pageId)}/nodes`);
  if (!res.ok) throw new Error(`fetchNodes: ${res.status}`);
  return res.json();
}

export async function createNode(
  pageId:   string,
  label:    string,
  parentId: string | null,
): Promise<ApiNode> {
  const res = await fetch(`${BASE}/api/pages/${encodeURIComponent(pageId)}/nodes`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ label, parent_id: parentId }),
  });
  if (!res.ok) throw new Error(`createNode: ${res.status}`);
  return res.json();
}

// ─── Writes (fire-and-forget; UI is the source of truth at runtime) ──────────

const reportFailure = (op: string) => (err: unknown) => {
  console.warn(`[api] ${op} failed`, err);
};

export function patchPosition(id: string, x: number, y: number): void {
  fetch(`${BASE}/api/nodes/${encodeURIComponent(id)}`, {
    method:  'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ x, y }),
  }).catch(reportFailure('patchPosition'));
}

export function patchStatus(id: string, status: Status): void {
  fetch(`${BASE}/api/nodes/${encodeURIComponent(id)}`, {
    method:  'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ status }),
  }).catch(reportFailure('patchStatus'));
}

export function patchDescription(id: string, description: string): void {
  fetch(`${BASE}/api/nodes/${encodeURIComponent(id)}`, {
    method:  'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ description }),
  }).catch(reportFailure('patchDescription'));
}

export function bulkPositions(items: PositionUpdate[]): void {
  if (items.length === 0) return;
  fetch(`${BASE}/api/nodes/positions`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(items),
  }).catch(reportFailure('bulkPositions'));
}

export function deleteNode(id: string): void {
  fetch(`${BASE}/api/nodes/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  }).catch(reportFailure('deleteNode'));
}

// ─── Events ───────────────────────────────────────────────────────────────────
// These are awaited (not fire-and-forget): the panel needs the returned row for
// the timeline, and a GET to populate history when a node is selected.

export async function fetchEvents(nodeId: string): Promise<ApiEvent[]> {
  const res = await fetch(`${BASE}/api/nodes/${encodeURIComponent(nodeId)}/events`);
  if (!res.ok) throw new Error(`fetchEvents: ${res.status}`);
  return res.json();
}

export interface PageEventsPoll {
  events: ApiEvent[];
  /** Opaque cursor to pass as `since` on the next poll. */
  cursor: string;
}

/**
 * Poll the page-wide event stream. Omit `since` on the first call to seed the
 * cursor (returns no events); pass the returned cursor on each subsequent call
 * to receive only events created since.
 */
export async function fetchPageEvents(pageId: string, since?: string): Promise<PageEventsPoll> {
  const qs  = since !== undefined ? `?since=${encodeURIComponent(since)}` : '';
  const res = await fetch(`${BASE}/api/pages/${encodeURIComponent(pageId)}/events${qs}`);
  if (!res.ok) throw new Error(`fetchPageEvents: ${res.status}`);
  return res.json();
}

/**
 * Fetch the full page-wide event history, newest first. Passes an empty cursor
 * (`since=`), which the backend treats as "before everything" → returns all
 * events (oldest-first), so we reverse for display.
 */
export async function fetchAllPageEvents(pageId: string): Promise<ApiEvent[]> {
  const { events } = await fetchPageEvents(pageId, '');
  return events.reverse();
}

export async function createEvent(
  nodeId:  string,
  payload: { status?: Status | null; note?: string; author?: string; label?: string | null },
): Promise<ApiEvent> {
  const res = await fetch(`${BASE}/api/nodes/${encodeURIComponent(nodeId)}/events`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`createEvent: ${res.status}`);
  return res.json();
}

// ─── Conversion helpers ──────────────────────────────────────────────────────

/**
 * Reconstruct the NodeData[] shape (with `children` arrays) from a flat list of
 * ApiNode rows. Children are sorted by their stored child_order.
 */
export function apiNodesToNodeData(rows: ApiNode[]): NodeData[] {
  const childrenByParent = new Map<string, { id: string; order: number }[]>();
  for (const r of rows) {
    if (r.parent_id) {
      const list = childrenByParent.get(r.parent_id) ?? [];
      list.push({ id: r.id, order: r.child_order });
      childrenByParent.set(r.parent_id, list);
    }
  }

  return rows.map(r => ({
    id:          r.id,
    label:       r.label,
    description: r.description,
    status:      r.status,
    children: (childrenByParent.get(r.id) ?? [])
      .sort((a, b) => a.order - b.order)
      .map(c => c.id),
  }));
}
